import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function LotteryResultPage() {
  const navigate = useNavigate();
  const [numbers, setNumbers] = useState([0, 0, 0, 0, 0, 0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadResult();
  }, []);

  const loadResult = async () => {
    try {
      const response = await axios.get(`${API}/lottery-result`);
      setNumbers(response.data.numbers);
    } catch (error) {
      toast.error("Failed to load lottery result");
    } finally {
      setLoading(false);
    }
  };

  const handleNumberChange = (index, value) => {
    const newNumbers = [...numbers];
    newNumbers[index] = parseInt(value) || 0;
    setNumbers(newNumbers);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/lottery-result`, { numbers });
      toast.success("Lottery result updated! All banners will now show the new numbers.");
    } catch (error) {
      toast.error("Failed to update lottery result");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      <nav className="glass-effect border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/")}
                data-testid="back-button"
                variant="ghost"
                className="hover:bg-white/5"
              >
                ← Back
              </Button>
              <h1 className="text-2xl font-bold text-foreground">Update Lottery Result</h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-result-button"
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-10 px-6 rounded-full font-medium shadow-[0_0_20px_rgba(16,185,129,0.3)]"
            >
              {saving ? "Saving..." : "Update Result"}
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="glass-effect border-white/10" data-testid="lottery-result-card">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-foreground">Current Lottery Numbers</CardTitle>
            <p className="text-center text-muted-foreground mt-2">
              Update the lottery result here. All banners will automatically display the new numbers.
            </p>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="flex justify-center gap-4 flex-wrap">
              {numbers.map((num, index) => (
                <div key={`ball-${index}`} className="flex flex-col items-center" data-testid={`number-input-${index}`}>
                  <Label className="mb-2 text-sm text-muted-foreground">Ball {index + 1}</Label>
                  <div className="relative">
                    <div className="lottery-number-ball shadow-[0_0_20px_rgba(16,185,129,0.4)]">
                      <Input
                        type="number"
                        min="0"
                        max="99"
                        value={num}
                        onChange={(e) => handleNumberChange(index, e.target.value)}
                        data-testid={`number-value-${index}`}
                        className="w-16 h-16 text-center text-2xl font-bold font-mono bg-transparent border-none text-primary-foreground p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-secondary/20 rounded-lg p-6 border border-white/10">
              <h3 className="font-semibold text-foreground mb-2">Instructions:</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-sm">
                <li>Enter 6 numbers for the lottery result</li>
                <li>Each number can be between 0 and 99</li>
                <li>Click "Update Result" to save and apply to all banners</li>
                <li>All public banner URLs will immediately show the updated numbers</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}